import React, { useCallback, useEffect, useState } from 'react'
import { Heading, Text, Button, Input, Select } from '../../components/ui'
import { BRAND_COLOR } from '../../constants'
import { helpdeskPermissionsService } from '../../services/helpdeskPermissions'
import { supabase } from '../../lib/supabase'
import {
  HelpdeskModule,
  HelpdeskPermission,
  HelpdeskPermissionWithDetails,
  HelpdeskUserGroup,
  CreateHelpdeskUserGroupData
} from '../../types'
import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'

type HelpdeskProfile = {
  id: string
  name?: string | null
  phone?: string | null
  is_active: boolean
  helpdesk_user_group_id?: string | null
}

type PermissionDraft = {
  can_show: boolean
  can_insert: boolean
  can_update: boolean
  can_delete: boolean
  can_view_values: boolean
}

const defaultPermissionDraft: PermissionDraft = {
  can_show: false,
  can_insert: false,
  can_update: false,
  can_delete: false,
  can_view_values: false
}

const defaultGroupForm: CreateHelpdeskUserGroupData & { is_active?: boolean } = {
  name: '',
  display_name: '',
  description: '',
  is_active: true
}

export const PortalAtendimentoPermissionsTab: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'grupos' | 'permissoes'>('grupos')

  const [groups, setGroups] = useState<HelpdeskUserGroup[]>([])
  const [modules, setModules] = useState<HelpdeskModule[]>([])
  const [groupPermissions, setGroupPermissions] = useState<Record<string, HelpdeskPermission[]>>({})
  const [helpdeskUsers, setHelpdeskUsers] = useState<HelpdeskProfile[]>([])

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [matrixGroupFilter, setMatrixGroupFilter] = useState<string>('all')
  const [matrixModuleFilter, setMatrixModuleFilter] = useState<string>('all')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [groupActiveTabs, setGroupActiveTabs] = useState<Record<string, 'permissions' | 'users'>>({})
  const [selectedGroupForPermission, setSelectedGroupForPermission] = useState<HelpdeskUserGroup | null>(null)
  const [selectedModuleForPermission, setSelectedModuleForPermission] = useState<HelpdeskModule | null>(null)
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)
  const [editingPermissions, setEditingPermissions] = useState<PermissionDraft>(defaultPermissionDraft)

  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<HelpdeskUserGroup | null>(null)
  const [groupForm, setGroupForm] = useState<typeof defaultGroupForm>(defaultGroupForm)

  const buildModuleTree = (input: HelpdeskModule[]): Array<HelpdeskModule & { children: HelpdeskModule[] }> => {
    const byId = new Map<string, HelpdeskModule & { children: HelpdeskModule[] }>(
      input.map((m) => [m.id, { ...m, children: [] }])
    )
    const roots: Array<HelpdeskModule & { children: HelpdeskModule[] }> = []
    byId.forEach((m) => {
      if (m.parent_module_id && byId.has(m.parent_module_id)) {
        byId.get(m.parent_module_id)!.children.push(m)
      } else {
        roots.push(m)
      }
    })
    const sortByOrder = (a: HelpdeskModule, b: HelpdeskModule) =>
      (a.order_index ?? 1e9) - (b.order_index ?? 1e9)
    byId.forEach((m) => m.children.sort(sortByOrder))
    roots.sort(sortByOrder)
    return roots
  }

  const getAccessLevel = (perm?: HelpdeskPermission): 'none' | 'view' | 'partial' | 'full' => {
    if (!perm || !perm.can_show) return 'none'
    const flags = [perm.can_insert, perm.can_update, perm.can_delete, perm.can_view_values]
    if (flags.every(Boolean)) return 'full'
    if (flags.every((f) => !f)) return 'view'
    return 'partial'
  }

  const loadAllGroupPermissions = async (sourceGroups: HelpdeskUserGroup[]) => {
    const map: Record<string, HelpdeskPermission[]> = {}
    await Promise.all(
      sourceGroups.map(async (group) => {
        const result = await helpdeskPermissionsService.getPermissionsByGroup(group.id)
        map[group.id] = (result.data || []).map((p: HelpdeskPermissionWithDetails) => ({
          id: p.id,
          helpdesk_user_group_id: p.helpdesk_user_group_id,
          module_id: p.module_id,
          can_show: !!p.can_show,
          can_insert: !!p.can_insert,
          can_update: !!p.can_update,
          can_delete: !!p.can_delete,
          can_view_values: !!p.can_view_values,
          created_at: p.created_at,
          updated_at: p.updated_at
        }))
      })
    )
    setGroupPermissions(map)
  }

  const loadHelpdeskUsers = async () => {
    const { data, error: usersError } = await supabase
      .from('profiles')
      .select('id, name, phone, is_active, helpdesk_user_group_id')
      .not('helpdesk_user_group_id', 'is', null)

    if (usersError) {
      setError(usersError.message || 'Erro ao carregar usuários do helpdesk.')
      setHelpdeskUsers([])
      return
    }

    setHelpdeskUsers((data || []) as HelpdeskProfile[])
  }

  const loadBaseData = useCallback(async () => {
    setLoading(true)
    setError(null)

    const [groupsRes, modulesRes] = await Promise.all([
      helpdeskPermissionsService.getGroups(),
      helpdeskPermissionsService.getActiveModules()
    ])

    if (!groupsRes.success) setError(groupsRes.error || 'Erro ao carregar grupos.')
    if (!modulesRes.success) setError(modulesRes.error || 'Erro ao carregar módulos.')

    const loadedGroups = groupsRes.data || []
    setGroups(loadedGroups)
    setModules(modulesRes.data || [])
    setExpandedGroups(new Set())
    const defaultTabs: Record<string, 'permissions' | 'users'> = {}
    loadedGroups.forEach((g: HelpdeskUserGroup) => { defaultTabs[g.id] = 'permissions' })
    setGroupActiveTabs(defaultTabs)
    await loadAllGroupPermissions(loadedGroups)
    await loadHelpdeskUsers()

    setLoading(false)
  }, [])

  useEffect(() => {
    loadBaseData()
  }, [loadBaseData])

  const openCreateGroup = () => {
    setEditingGroup(null)
    setGroupForm(defaultGroupForm)
    setIsGroupModalOpen(true)
  }

  const openEditGroup = (group: HelpdeskUserGroup) => {
    setEditingGroup(group)
    setGroupForm({
      name: group.name,
      display_name: group.display_name,
      description: group.description || '',
      is_active: group.is_active
    })
    setIsGroupModalOpen(true)
  }

  const saveGroup = async () => {
    setSaving(true)
    setError(null)

    const payload: CreateHelpdeskUserGroupData & { is_active?: boolean } = {
      name: groupForm.name.trim(),
      display_name: groupForm.display_name.trim(),
      description: groupForm.description?.trim() || undefined,
      is_active: !!groupForm.is_active
    }

    const response = editingGroup
      ? await helpdeskPermissionsService.updateGroup(editingGroup.id, payload)
      : await helpdeskPermissionsService.createGroup(payload)

    if (!response.success) {
      setError(response.error || 'Erro ao salvar grupo.')
      setSaving(false)
      return
    }

    setIsGroupModalOpen(false)
    setEditingGroup(null)
    await loadBaseData()
    setSaving(false)
  }

  const deleteGroup = async (group: HelpdeskUserGroup) => {
    if (!window.confirm(`Excluir o grupo "${group.display_name}"?`)) return
    setError(null)
    const response = await helpdeskPermissionsService.deleteGroup(group.id)
    if (!response.success) {
      setError(response.error || 'Erro ao excluir grupo.')
      return
    }
    await loadBaseData()
  }

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const expandAllGroups = () => {
    setExpandedGroups(new Set(groups.map((g) => g.id)))
  }

  const collapseAllGroups = () => {
    setExpandedGroups(new Set())
  }

  const openPermissionModal = (groupId: string, moduleId: string) => {
    const group = groups.find((g) => g.id === groupId)
    const module = modules.find((m) => m.id === moduleId)
    if (!group || !module) return

    const existing = (groupPermissions[groupId] || []).find((p) => p.module_id === moduleId)
    setSelectedGroupForPermission(group)
    setSelectedModuleForPermission(module)
    setEditingPermissions(
      existing
        ? {
            can_show: !!existing.can_show,
            can_insert: !!existing.can_insert,
            can_update: !!existing.can_update,
            can_delete: !!existing.can_delete,
            can_view_values: !!existing.can_view_values
          }
        : { ...defaultPermissionDraft }
    )
    setIsPermissionModalOpen(true)
  }

  const handleSelectAllPermissions = () => {
    setEditingPermissions({
      can_show: true,
      can_insert: true,
      can_update: true,
      can_delete: true,
      can_view_values: true
    })
  }

  const handleDeselectAllPermissions = () => {
    setEditingPermissions({ ...defaultPermissionDraft })
  }

  const savePermissionModal = async () => {
    if (!selectedGroupForPermission || !selectedModuleForPermission) return

    setSaving(true)
    setError(null)
    const response = await helpdeskPermissionsService.upsertPermission({
      helpdesk_user_group_id: selectedGroupForPermission.id,
      module_id: selectedModuleForPermission.id,
      can_show: editingPermissions.can_show,
      can_insert: editingPermissions.can_insert,
      can_update: editingPermissions.can_update,
      can_delete: editingPermissions.can_delete,
      can_view_values: editingPermissions.can_view_values
    })

    if (!response.success) {
      setError(response.error || 'Erro ao salvar permissão.')
      setSaving(false)
      return
    }

    await loadAllGroupPermissions(groups)
    setIsPermissionModalOpen(false)
    setSaving(false)
  }

  const getUsersByHelpdeskGroup = (groupId: string) =>
    helpdeskUsers.filter((u) => u.helpdesk_user_group_id === groupId)

  const sectionTabClass = (active: boolean) =>
    `py-4 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  return (
    <div className="space-y-6">
      <div>
        <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
          Grupos e Permissões
        </Heading>
        <Text size="sm" color="muted" className="mt-1">
          Gestão de grupos e permissões para os módulos do Portal de atendimento.
        </Text>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8" aria-label="Abas de grupos e permissões">
          <button
            type="button"
            className={sectionTabClass(activeSection === 'grupos')}
            onClick={() => setActiveSection('grupos')}
          >
            Grupos
          </button>
          <button
            type="button"
            className={sectionTabClass(activeSection === 'permissoes')}
            onClick={() => setActiveSection('permissoes')}
          >
            Permissões
          </button>
        </nav>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {activeSection === 'grupos' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <Text size="sm" className="text-gray-700 dark:text-gray-300">
              Grupos de acesso do contexto helpdesk
            </Text>
            <Button
              onClick={openCreateGroup}
              className="text-white flex items-center gap-2"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              <PlusIcon className="h-5 w-5" />
              Novo Grupo
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Grupo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Nome interno</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Descrição</th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Ativo</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      Carregando grupos...
                    </td>
                  </tr>
                ) : groups.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhum grupo cadastrado.
                    </td>
                  </tr>
                ) : (
                  groups.map((group) => (
                    <tr key={group.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{group.display_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{group.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{group.description || '-'}</td>
                      <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">{group.is_active ? 'Sim' : 'Não'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEditGroup(group)}
                            className="p-2 rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            title="Editar grupo"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteGroup(group)}
                            className="p-2 rounded-md text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            title="Excluir grupo"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSection === 'permissoes' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Matriz de Permissões por Grupo
            </h3>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filtrar por Grupo
                </label>
                <Select value={matrixGroupFilter} onChange={(e) => setMatrixGroupFilter(e.target.value)}>
                  <option value="all">Todos os grupos</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.display_name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filtrar por Módulo
                </label>
                <Select value={matrixModuleFilter} onChange={(e) => setMatrixModuleFilter(e.target.value)}>
                  <option value="all">Todos os módulos</option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.id}>
                      {module.display_name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                {(matrixGroupFilter !== 'all' || matrixModuleFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setMatrixGroupFilter('all')
                      setMatrixModuleFilter('all')
                    }}
                    className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Limpar filtros
                  </button>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={expandAllGroups}
                    className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:text-green-400 rounded-md font-medium transition-colors"
                  >
                    Expandir Todos
                  </button>
                  <button
                    onClick={collapseAllGroups}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 rounded-md font-medium transition-colors"
                  >
                    Retrair Todos
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {groups
              .filter((group) => matrixGroupFilter === 'all' || group.id === matrixGroupFilter)
              .map((group) => {
                const groupPermissionsList = groupPermissions[group.id] || []
                const isExpanded = expandedGroups.has(group.id)
                const filteredModules = modules
                  .filter((module) => matrixModuleFilter === 'all' || module.id === matrixModuleFilter)
                  .sort((a, b) => (a.order_index ?? 1e9) - (b.order_index ?? 1e9))
                const modulesWithAccess = groupPermissionsList.filter((p) => p.can_show).length
                const totalModules = filteredModules.length

                return (
                  <div key={group.id} className="border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-md bg-white dark:bg-gray-800">
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                      onClick={() => toggleGroupExpansion(group.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <UserGroupIcon className="h-5 w-5 text-gray-500" />
                            <span className="font-medium text-gray-900 dark:text-white text-lg">
                              {group.display_name}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {modulesWithAccess} de {totalModules} módulos com acesso
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {isExpanded ? 'Retrair' : 'Expandir'}
                          </div>
                          {isExpanded ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t-2 border-gray-300 dark:border-gray-600 transition-all duration-300 bg-gray-50 dark:bg-gray-700/30">
                        <div className="border-b-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600/50">
                          <nav className="flex space-x-8 px-4" aria-label="Tabs">
                            <button
                              onClick={() => setGroupActiveTabs((prev) => ({ ...prev, [group.id]: 'permissions' }))}
                              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                                (groupActiveTabs[group.id] || 'permissions') === 'permissions'
                                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                              }`}
                            >
                              Permissões
                            </button>
                            <button
                              onClick={() => setGroupActiveTabs((prev) => ({ ...prev, [group.id]: 'users' }))}
                              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                                groupActiveTabs[group.id] === 'users'
                                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                              }`}
                            >
                              Usuários ({getUsersByHelpdeskGroup(group.id).length})
                            </button>
                          </nav>
                        </div>

                        {(groupActiveTabs[group.id] || 'permissions') === 'permissions' && (
                        <div className="p-6 bg-gray-50 dark:bg-gray-700/30">
                          <div className="space-y-3">
                            {(() => {
                              const tree = buildModuleTree(filteredModules)
                              return tree.map((parentModule) => {
                                const children = parentModule.children || []
                                const allItems = children.length > 0 ? children : [parentModule]
                                const isRootOnly = children.length === 0

                                const accessCounts = { none: 0, view: 0, partial: 0, full: 0 }
                                allItems.forEach((mod) => {
                                  const perm = groupPermissionsList.find((p) => p.module_id === mod.id)
                                  const level = getAccessLevel(perm)
                                  accessCounts[level]++
                                })
                                const totalItems = allItems.length
                                const withAccess = totalItems - accessCounts.none

                                return (
                                  <div key={parentModule.id} className="rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
                                    {!isRootOnly && (() => {
                                      const parentPerms = groupPermissionsList.find((p) => p.module_id === parentModule.id)
                                      const parentLevel = getAccessLevel(parentPerms)
                                      const levelColors = {
                                        none: 'bg-gray-100 dark:bg-gray-700/60',
                                        view: 'bg-gray-100 dark:bg-gray-700/60',
                                        partial: 'bg-gray-100 dark:bg-gray-700/60',
                                        full: 'bg-gray-100 dark:bg-gray-700/60'
                                      }
                                      const dotColors = {
                                        none: 'bg-red-400',
                                        view: 'bg-blue-400',
                                        partial: 'bg-yellow-400',
                                        full: 'bg-green-500'
                                      }
                                      const levelLabel = {
                                        none: 'Sem acesso',
                                        view: 'Só visualizar',
                                        partial: 'Parcial',
                                        full: 'Acesso total'
                                      }
                                      return (
                                        <div
                                          className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors hover:brightness-95 dark:hover:brightness-110 border-b border-gray-200 dark:border-gray-600 ${levelColors[parentLevel]}`}
                                          onClick={() => openPermissionModal(group.id, parentModule.id)}
                                        >
                                          <div className="flex items-center gap-2 min-w-0">
                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[parentLevel]}`} />
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                                              {parentModule.display_name}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                            <span className={`text-xs font-medium ${
                                              parentLevel === 'none' ? 'text-red-500 dark:text-red-400' :
                                              parentLevel === 'view' ? 'text-blue-600 dark:text-blue-400' :
                                              parentLevel === 'partial' ? 'text-yellow-600 dark:text-yellow-400' :
                                              'text-green-600 dark:text-green-400'
                                            }`}>
                                              {levelLabel[parentLevel]}
                                            </span>
                                            {parentPerms && (
                                              <div className="flex gap-1">
                                                {parentPerms.can_show && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 text-xs rounded">Ver</span>}
                                                {parentPerms.can_view_values && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-xs rounded">Valores</span>}
                                                {parentPerms.can_insert && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 text-xs rounded">Criar</span>}
                                                {parentPerms.can_update && <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 text-xs rounded">Editar</span>}
                                                {parentPerms.can_delete && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 text-xs rounded">Excluir</span>}
                                              </div>
                                            )}
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-1 ${
                                              withAccess === 0
                                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                : withAccess === totalItems
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                                            }`}>
                                              {withAccess}/{totalItems} filhos
                                            </span>
                                            <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 -rotate-90" />
                                          </div>
                                        </div>
                                      )
                                    })()}

                                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                      {allItems.map((mod, idx) => {
                                        const perms = groupPermissionsList.find((p) => p.module_id === mod.id)
                                        const level = getAccessLevel(perms)
                                        const levelColors = isRootOnly
                                          ? {
                                              none: 'bg-gray-100 dark:bg-gray-700/60',
                                              view: 'bg-gray-100 dark:bg-gray-700/60',
                                              partial: 'bg-gray-100 dark:bg-gray-700/60',
                                              full: 'bg-gray-100 dark:bg-gray-700/60'
                                            }
                                          : {
                                              none: 'bg-red-50 dark:bg-red-900/10',
                                              view: 'bg-blue-50 dark:bg-blue-900/10',
                                              partial: 'bg-yellow-50 dark:bg-yellow-900/10',
                                              full: 'bg-green-50 dark:bg-green-900/10'
                                            }
                                        const dotColors = {
                                          none: 'bg-red-400',
                                          view: 'bg-blue-400',
                                          partial: 'bg-yellow-400',
                                          full: 'bg-green-500'
                                        }
                                        const levelLabel = {
                                          none: 'Sem acesso',
                                          view: 'Só visualizar',
                                          partial: 'Parcial',
                                          full: 'Acesso total'
                                        }
                                        return (
                                          <div
                                            key={mod.id}
                                            className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors hover:brightness-95 dark:hover:brightness-110 ${levelColors[level]} ${isRootOnly && idx === 0 ? '' : 'pl-8'}`}
                                            onClick={() => openPermissionModal(group.id, mod.id)}
                                          >
                                            <div className="flex items-center gap-2 min-w-0">
                                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[level]}`} />
                                              <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                                {mod.display_name}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                              <span className={`text-xs font-medium ${
                                                level === 'none' ? 'text-red-500 dark:text-red-400' :
                                                level === 'view' ? 'text-blue-600 dark:text-blue-400' :
                                                level === 'partial' ? 'text-yellow-600 dark:text-yellow-400' :
                                                'text-green-600 dark:text-green-400'
                                              }`}>
                                                {levelLabel[level]}
                                              </span>
                                              {perms && (
                                                <div className="flex gap-1">
                                                  {perms.can_show && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 text-xs rounded">Ver</span>}
                                                  {perms.can_view_values && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-xs rounded">Valores</span>}
                                                  {perms.can_insert && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 text-xs rounded">Criar</span>}
                                                  {perms.can_update && <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 text-xs rounded">Editar</span>}
                                                  {perms.can_delete && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 text-xs rounded">Excluir</span>}
                                                </div>
                                              )}
                                              <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 -rotate-90" />
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )
                              })
                            })()}
                          </div>
                        </div>
                        )}

                        {groupActiveTabs[group.id] === 'users' && (
                          <div className="p-6 bg-gray-50 dark:bg-gray-700/30">
                            <div className="space-y-3">
                              {getUsersByHelpdeskGroup(group.id).length > 0 ? (
                                getUsersByHelpdeskGroup(group.id).map((user) => (
                                  <div
                                    key={user.id}
                                    className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-600/50 rounded-lg border border-gray-200 dark:border-gray-600"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                                        <UserGroupIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                      </div>
                                      <div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                          {user.name || 'Usuário sem nome'}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                          {user.phone || 'Sem telefone'}
                                        </div>
                                      </div>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      user.is_active
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                    }`}>
                                      {user.is_active ? 'Ativo' : 'Inativo'}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                  Nenhum usuário vinculado a este grupo.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
      )}

      {isGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl">
                {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
              </Heading>
              <Button variant="ghost" onClick={() => setIsGroupModalOpen(false)}>
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome interno</label>
                <Input
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder="ex: helpdesk_gestor"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome de exibição</label>
                <Input
                  value={groupForm.display_name}
                  onChange={(e) => setGroupForm({ ...groupForm, display_name: e.target.value })}
                  placeholder="ex: Gestor Helpdesk"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descrição</label>
                <Input
                  value={groupForm.description || ''}
                  onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  placeholder="Opcional"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="group_is_active"
                  type="checkbox"
                  checked={!!groupForm.is_active}
                  onChange={(e) => setGroupForm({ ...groupForm, is_active: e.target.checked })}
                />
                <label htmlFor="group_is_active" className="text-sm text-gray-700 dark:text-gray-300">
                  Grupo ativo
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setIsGroupModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={saveGroup}
                disabled={saving || !groupForm.name.trim() || !groupForm.display_name.trim()}
                className="text-white"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {saving ? 'Salvando...' : editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isPermissionModalOpen && selectedGroupForPermission && selectedModuleForPermission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Configurar Permissões
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  {selectedGroupForPermission.display_name} - {selectedModuleForPermission.display_name}
                </Text>
              </div>
              <Button onClick={() => setIsPermissionModalOpen(false)} variant="ghost" size="sm">
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
              <Text size="sm" className="text-gray-600 dark:text-gray-400">
                Seleção rápida:
              </Text>
              <div className="flex items-center gap-2">
                <Button onClick={handleSelectAllPermissions} variant="outline" size="sm" className="text-xs">
                  Marcar Todos
                </Button>
                <Button onClick={handleDeselectAllPermissions} variant="outline" size="sm" className="text-xs">
                  Desmarcar Todos
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_show"
                  checked={editingPermissions.can_show}
                  onChange={(e) => setEditingPermissions((prev) => ({ ...prev, can_show: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                />
                <label htmlFor="can_show" className="text-sm font-medium text-gray-900 dark:text-white">
                  Visualizar
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_view_values"
                  checked={editingPermissions.can_view_values}
                  onChange={(e) => setEditingPermissions((prev) => ({ ...prev, can_view_values: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded"
                />
                <label htmlFor="can_view_values" className="text-sm font-medium text-gray-900 dark:text-white">
                  Ver valores
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_insert"
                  checked={editingPermissions.can_insert}
                  onChange={(e) => setEditingPermissions((prev) => ({ ...prev, can_insert: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                />
                <label htmlFor="can_insert" className="text-sm font-medium text-gray-900 dark:text-white">
                  Criar
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_update"
                  checked={editingPermissions.can_update}
                  onChange={(e) => setEditingPermissions((prev) => ({ ...prev, can_update: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                />
                <label htmlFor="can_update" className="text-sm font-medium text-gray-900 dark:text-white">
                  Editar
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="can_delete"
                  checked={editingPermissions.can_delete}
                  onChange={(e) => setEditingPermissions((prev) => ({ ...prev, can_delete: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                />
                <label htmlFor="can_delete" className="text-sm font-medium text-gray-900 dark:text-white">
                  Excluir
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button onClick={() => setIsPermissionModalOpen(false)} variant="outline">
                Cancelar
              </Button>
              <Button onClick={savePermissionModal} style={{ backgroundColor: BRAND_COLOR }} className="text-white">
                Salvar Permissões
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
